import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Center,
  Spinner,
  Card,
  IconButton,
  Badge,
  VStack,
  SimpleGrid,
  ModalCloseButton,
  Circle,
  Icon,
  FormControl,
  FormLabel,
  Select,
  Tooltip
} from "@chakra-ui/react";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender
} from "@tanstack/react-table";
import * as React from "react";
import { employeeService } from '../../../../services/employeeService';
import { authService } from '../../../../services/authService';
import { MdDelete, MdPerson, MdChevronLeft, MdChevronRight, MdStar, MdWarning, MdExpandMore, MdExpandLess, MdContentCopy } from 'react-icons/md';

const columnHelper = createColumnHelper();

export default function TopCreatorTable({ onEmployeeCountChange }) {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const pageSize = 5;
  const [currentPage, setCurrentPage] = React.useState(0);
  const [expandedRows, setExpandedRows] = React.useState(new Set()); // Açık satırlar
  const [reviewPages, setReviewPages] = React.useState({}); // Her çalışan için yorum sayfası
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newName, setNewName] = React.useState("");
  const [newPhone, setNewPhone] = React.useState("");
  const [newPosition, setNewPosition] = React.useState("");
  const [newRole, setNewRole] = React.useState("staff");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const bgColor = useColorModeValue("white", "navy.800");
  const hoverBg = useColorModeValue("gray.50", "navy.700");
  const toast = useToast();
  const currentUser = authService.getCurrentUser();
  const isStaff = currentUser?.role === 'staff' && currentUser?.userType === 'employee';

  const paginatedData = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }, [data, currentPage, pageSize]);

  const columns = React.useMemo(() => {
    const baseColumns = [
      columnHelper.accessor("name", {
        header: "Adı Soyadı",
        cell: (info) => (
          <Text color={textColor} fontWeight="500" fontSize="md" textAlign="center">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("phone", {
        header: "Telefon Numarası",
        cell: (info) => (
          <Text color={textColor} fontSize="md" textAlign="center">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("position", {
        header: "Çalıştığı Pozisyon",
        cell: (info) => (
          <Text color={textColor} fontSize="md" textAlign="center">
            {info.getValue()}
          </Text>
        ),
      }),
      // Email sütunu - sadece staff'lar için göster (manager'ların email'i yok)
      columnHelper.accessor("email", {
        header: "Email",
        cell: (info) => {
          const email = info.getValue();
          const role = info.row.original.role;
          return (
            <Text color={textColor} fontSize="md" textAlign="center">
              {email || (role === 'manager' ? '-' : '-')}
            </Text>
          );
        },
      }),
    ];

    // Staff ise ortalama puan sütununu ekleme
    if (!isStaff) {
      baseColumns.push(
        columnHelper.accessor("rating", {
          header: "Ortalama Puan",
          cell: (info) => {
            const rating = info.row.original.rating;
            const avgRating = rating?.averageRating || 0;
            const totalRatings = rating?.totalRatings || 0;
            const isLowRating = avgRating > 0 && avgRating < 3;
            
            return (
              <Flex align="center" gap={2} justify="center">
                {totalRatings > 0 ? (
                  <>
                    <Icon as={MdStar} color={isLowRating ? "orange.500" : "yellow.400"} />
                    <Text 
                      color={isLowRating ? "orange.500" : textColor} 
                      fontSize="md"
                      fontWeight={isLowRating ? "bold" : "500"}
                    >
                      {avgRating.toFixed(1)}
                    </Text>
                    <Text color="gray.500" fontSize="sm">
                      ({totalRatings})
                    </Text>
                    {isLowRating && (
                      <Icon as={MdWarning} color="orange.500" />
                    )}
                  </>
                ) : (
                  <Text color="gray.400" fontSize="sm">
                    0
                  </Text>
                )}
              </Flex>
            );
          },
        })
      );
    }

    // Staff ise işlemler sütununu ekleme
    if (!isStaff) {
      baseColumns.push(
        columnHelper.accessor("actions", {
          header: "İşlemler",
          cell: (info) => (
            <Flex justify="center"> 
            <IconButton
                icon={<MdDelete />}
                onClick={() => handleDelete(info.row.original._id)}
                colorScheme="red"
                size="sm"
                aria-label="Çalışan Sil"
              />
            </Flex>
          ),
        })
      );
    }

    return baseColumns;
  }, [textColor, isStaff]);

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  React.useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeService.getEmployees();

      if (response && response.success) {
        const employees = response.data || [];
        setData(employees);
        // Çalışan sayısını parent componente gönder
        if (onEmployeeCountChange) {
          onEmployeeCountChange(employees.length);
        }
      } else {
        // API yanıtı başarılı değilse, response.data yerine doğrudan response'u kontrol et
        if (Array.isArray(response)) {
          setData(response);
          if (onEmployeeCountChange) {
            onEmployeeCountChange(response.length);
          }
        } else {
          throw new Error(response?.message || 'Veri alınamadı');
        }
      }
    } catch (error) {
      console.error('Çalışan getirme hatası:', error);
      toast({
        title: "Hata",
        description: error.message || "Çalışanlar yüklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
      setData([]); // Hata durumunda boş dizi
    } finally {
      setLoading(false);
    }
  };

  const onAddNewRow = async () => {
    try {
      // Validasyon
      if (!newName.trim() || !newPhone.trim() || !newPosition.trim()) {
        toast({
          title: "Hata",
          description: "Tüm alanları doldurun",
          status: "error",
          duration: 3000,
        });
        return;
      }
      
      const restaurantId = localStorage.getItem('restaurant');
      const newEmployee = {
        name: newName.trim(),
        phone: newPhone,
        position: newPosition.trim(),
        role: newRole,
        restaurantId: restaurantId
      };

      const response = await employeeService.createEmployee(newEmployee);
      
      // Sadece Staff ise email ve şifre bilgisini göster
      // Manager'lar mevcut business login bilgilerini kullanır
      let description = "Yeni çalışan eklendi";
      if (newRole === 'staff' && response.data?.loginInfo) {
        const email = response.data.loginInfo.email;
        const password = response.data.loginInfo.password;
        description = `Çalışan eklendi. Email: ${email}, Şifre: ${password} (${password.length} karakter)`;
      } else if (newRole === 'manager') {
        description = 'Yönetici eklendi. Mevcut işletme giriş bilgileri ile giriş yapabilir.';
      }
      
      toast({
        title: "Başarılı",
        description: description,
        status: "success",
        duration: 5000,
      });

      fetchEmployees();
      onClose();
      setNewName("");
      setNewPhone("");
      setNewPosition("");
      setNewRole("staff");
    } catch (error) {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Çalışan eklenirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    
    // Sadece rakamları al
    const phoneNumber = value.replace(/[^\d]/g, '');
    
    // Formatlama
    if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    }
    if (phoneNumber.length <= 10) {
      return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3, 10)}`;
  };

  const handlePhoneChange = (value) => {
    // Maksimum 10 rakam olacak şekilde sınırla
    const numericValue = value.replace(/[^\d]/g, '').slice(0, 10);
    const formattedValue = formatPhoneNumber(numericValue);
    setNewPhone(formattedValue);
  };

  const handleDelete = async (id) => {
    try {
      await employeeService.deleteEmployee(id);
      
      toast({
        title: "Başarılı",
        description: "Çalışan silindi",
        status: "success",
        duration: 2000,
      });
      
      fetchEmployees();
    } catch (error) {
      toast({
        title: "Hata",
        description: "Çalışan silinirken bir hata oluştu",
        status: "error",
        duration: 3000,
      });
    }
  };

  const totalPages = Math.ceil(data.length / pageSize);

  // Satırı aç/kapat
  const toggleRow = (employeeId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(employeeId)) {
      newExpanded.delete(employeeId);
    } else {
      newExpanded.add(employeeId);
    }
    setExpandedRows(newExpanded);
  };

  // Yorum sayfasını değiştir
  const setReviewPage = (employeeId, page) => {
    setReviewPages(prev => ({
      ...prev,
      [employeeId]: page
    }));
  };

  // Çalışanın yorumlarını sayfalara böl
  const getPaginatedReviews = (reviews, employeeId) => {
    const page = reviewPages[employeeId] || 0;
    const start = page * 3;
    const end = start + 3;
    return {
      paginated: reviews.slice(start, end),
      currentPage: page,
      totalPages: Math.ceil(reviews.length / 3),
      totalReviews: reviews.length
    };
  };

  return (
    <Card
      direction="column"
      w="100%"
      px="25px"
      py="20px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Flex px="5px" justify="space-between" mb="20px" align="center">
        <Box>
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Çalışanlar
          </Text>
          <Text fontSize="xs" color="gray.500" mt={1}>
            * Emailler sadece Menomsoft Business girişleri için geçerlidir
          </Text>
        </Box>
        {!isStaff && (
          <Button onClick={onOpen} colorScheme="brand" size="sm">
            Yeni Çalışan Ekle
          </Button>
        )}
      </Flex>

      {loading ? (
        <Center py={10}>
          <Spinner size="xl" color="brand.500" />
        </Center>
      ) : data.length === 0 ? (
        <Center py={10} flexDirection="column" gap={4}>
          <Circle size="60px" bg="gray.100">
            <Icon as={MdPerson} boxSize="30px" color="gray.500" />
          </Circle>
          <Text color="gray.500">Henüz çalışan bulunmuyor</Text>
          {!isStaff && (
            <Button onClick={onOpen} colorScheme="brand" size="sm">
              İlk Çalışanı Ekle
            </Button>
          )}
        </Center>
      ) : (
        <>
          <Box
            overflowX="auto"
            sx={{
              "&::-webkit-scrollbar": {
                height: "8px",
                borderRadius: "8px",
                backgroundColor: `rgba(0, 0, 0, 0.05)`,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: `rgba(0, 0, 0, 0.1)`,
                borderRadius: "8px",
              },
            }}
          >
            <Table variant="simple" color="gray.500" mb="24px">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        borderColor={borderColor}
                        px="20px"
                        py="12px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {header.isPlaceholder ? null : (
                          <Text
                            justifyContent="space-between"
                            align="center"
                            fontSize={{ sm: "10px", lg: "12px" }}
                            color="gray.400"
                            fontWeight="600"
                            noOfLines={1}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </Text>
                        )}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => {
                  const employee = row.original;
                  const employeeId = employee._id;
                  const isExpanded = expandedRows.has(employeeId);
                  const reviews = employee.rating?.reviews || [];
                  const sortedReviews = [...reviews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                  const { paginated, currentPage, totalPages, totalReviews } = getPaginatedReviews(sortedReviews, employeeId);
                  
                  return (
                    <React.Fragment key={row.id}>
                      <Tr
                        cursor="pointer"
                        onClick={() => toggleRow(employeeId)}
                        _hover={{ bg: hoverBg }}
                        bg={isExpanded ? hoverBg : bgColor}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <Td
                            key={cell.id}
                            fontSize={{ sm: "14px" }}
                            borderColor={borderColor}
                            py="12px"
                            px="20px"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {cell.column.id === 'name' && (
                              <Flex align="center" gap={12}>
                                <Icon
                                  as={isExpanded ? MdExpandLess : MdExpandMore}
                                  color="gray.500"
                                  boxSize="20px"
                                />
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Flex>
                            )}
                            {cell.column.id !== 'name' && flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Td>
                        ))}
                      </Tr>
                      {isExpanded && !isStaff && (
                        <Tr>
                          <Td colSpan={isStaff ? 4 : 6} p={0} borderColor={borderColor}>
                            <Box bg={bgColor} p={4} borderTop="1px solid" borderColor={borderColor}>
                              {/* Staff için email ve şifre bilgisi */}
                              {employee.role === 'staff' && employee.email && (
                                <Box mb={4} p={3} bg="blue.50" borderRadius="md" borderLeft="3px solid" borderColor="blue.400">
                                  <Text fontWeight="bold" fontSize="sm" color={textColor} mb={2}>
                                    Giriş Bilgileri
                                  </Text>
                                  <VStack align="start" spacing={2}>
                                    <Box>
                                      <Text fontSize="sm" color={textColor} mb={1}>
                                        <Text as="span" fontWeight="bold">Email:</Text> {employee.email}
                                      </Text>
                                    </Box>
                                    {employee.name && employee.phone && (
                                      <Box>
                                        <Text fontSize="sm" color={textColor} mb={1}>
                                          <Text as="span" fontWeight="bold">Şifre:</Text>
                                        </Text>
                                        {(() => {
                                          const nameInitials = employee.name
                                            .split(' ')
                                            .map(n => n.charAt(0).toUpperCase())
                                            .join('');
                                          const initials = nameInitials.length >= 2 ? nameInitials : employee.name.substring(0, 2).toUpperCase();
                                          const cleanedPhone = employee.phone.replace(/[^\d]/g, '');
                                          const phoneLast6 = cleanedPhone.slice(-6);
                                          const calculatedPassword = `${initials}${phoneLast6}`;
                                          
                                          const handleCopyPassword = () => {
                                            navigator.clipboard.writeText(calculatedPassword);
                                            toast({
                                              title: "Kopyalandı",
                                              description: "Şifre panoya kopyalandı",
                                              status: "success",
                                              duration: 2000,
                                            });
                                          };
                                          
                                          return (
                                            <Flex align="center" gap={2}>
                                              <Box flex={1} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="blue.200">
                                                <Text fontSize="md" fontWeight="bold" color="blue.600" fontFamily="mono">
                                                  {calculatedPassword}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500" mt={1}>
                                                  Format: "{initials}" + "{phoneLast6}"
                                                </Text>
                                              </Box>
                                              <IconButton
                                                icon={<MdContentCopy />}
                                                size="sm"
                                                colorScheme="blue"
                                                variant="outline"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleCopyPassword();
                                                }}
                                                aria-label="Şifreyi Kopyala"
                                              />
                                            </Flex>
                                          );
                                        })()}
                                      </Box>
                                    )}
                                  </VStack>
                                </Box>
                              )}
                              {sortedReviews.length === 0 ? (
                                <Text color="gray.500" textAlign="center" py={4}>
                                  Henüz değerlendirme yapılmamış
                                </Text>
                              ) : (
                                <VStack align="stretch" spacing={3}>
                                  <Text fontWeight="bold" fontSize="md" color={textColor}>
                                    Değerlendirmeler ({totalReviews})
                                  </Text>
                                  {paginated.map((review, idx) => (
                                    <Box
                                      key={idx}
                                      p={3}
                                      bg={hoverBg}
                                      borderRadius="md"
                                      borderLeft="3px solid"
                                      borderColor={review.rating >= 4 ? "green.400" : review.rating >= 3 ? "yellow.400" : "red.400"}
                                    >
                                      <Flex justify="space-between" align="start" mb={2}>
                                        <Flex align="center" gap={2}>
                                          <Icon as={MdStar} color="yellow.400" boxSize="16px" />
                                          <Text fontWeight="bold" fontSize="sm" color={textColor}>
                                            {review.rating}/5
                                          </Text>
                                        </Flex>
                                        <Text fontSize="xs" color="gray.500">
                                          {new Date(review.createdAt).toLocaleDateString('tr-TR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}
                                        </Text>
                                      </Flex>
                                      {review.comment && review.comment.trim() && (
                                        <Text fontSize="sm" color={textColor} mt={2}>
                                          {review.comment}
                                        </Text>
                                      )}
                                    </Box>
                                  ))}
                                  {totalPages > 1 && (
                                    <Flex justify="center" align="center" gap={2} mt={4}>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setReviewPage(employeeId, Math.max(0, currentPage - 1));
                                        }}
                                        isDisabled={currentPage === 0}
                                        leftIcon={<MdChevronLeft />}
                                      >
                                        Önceki
                                      </Button>
                                      <Text fontSize="sm" color={textColor}>
                                        Sayfa {currentPage + 1} / {totalPages}
                                      </Text>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setReviewPage(employeeId, Math.min(totalPages - 1, currentPage + 1));
                                        }}
                                        isDisabled={currentPage >= totalPages - 1}
                                        rightIcon={<MdChevronRight />}
                                      >
                                        Sonraki
                                      </Button>
                                    </Flex>
                                  )}
                                </VStack>
                              )}
                            </Box>
                          </Td>
                        </Tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Flex justify="center" align="center" w="100%" mb={4}>
              <Button
                variant="ghost"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                isDisabled={currentPage === 0}
                leftIcon={<MdChevronLeft />}
                size="sm"
              >
                Önceki
              </Button>
              <Text mx={4} fontSize="sm">
                Sayfa {currentPage + 1} / {totalPages}
              </Text>
              <Button
                variant="ghost"
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                isDisabled={currentPage === totalPages - 1}
                rightIcon={<MdChevronRight />}
                size="sm"
              >
                Sonraki
              </Button>
            </Flex>
          )}
        </>
      )}

      {/* Yeni Çalışan Ekleme Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent mx={4}>
          <ModalHeader>Yeni Çalışan Ekle</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex direction="column" gap={4}>
              <Box>
                <Text mb={2} fontWeight="medium">Adı Soyadı</Text>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Çalışan adı ve soyadı"
                />
              </Box>
              
              <Box>
                <Text mb={2} fontWeight="medium">Telefon Numarası</Text>
                <Input
                  value={newPhone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="(555) 123 4567"
                />
              </Box>
              
              <Box>
                <Text mb={2} fontWeight="medium">Çalıştığı Pozisyon</Text>
                <Input
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  placeholder="Garson, Şef, vb."
                />
              </Box>
              
              <FormControl>
                <FormLabel fontWeight="medium">Yetki Seviyesi</FormLabel>
                <Select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="staff">Çalışan (Staff)</option>
                  <option value="manager">Yönetici (Manager)</option>
                </Select>
                <Text mt={1} fontSize="sm" color="gray.500">
                  {newRole === 'staff' 
                    ? 'Çalışan olarak eklenecek. Email ve şifre otomatik oluşturulacak.'
                    : 'Yönetici olarak eklenecek. Mevcut işletme giriş bilgileri ile giriş yapabilir.'}
                </Text>
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="brand" mr={3} onClick={onAddNewRow}>
              Ekle
            </Button>
            <Button onClick={onClose}>İptal</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
